import { withSelect } from "@wordpress/data";
import { Fragment } from "@wordpress/element";
import classnames from "classnames";
import React from "react";
import { Icon } from "../../components";
import {
	useBlockStyle,
	useClientId,
	useCopyPasteStyles,
	useDeviceType,
} from "../../hooks";
import { EditProps } from "../../types";
import "../assets/sass/blocks/post-video/style.scss";
import InspectorControls from "./components/InspectorControls";
import defaultThumbnail from "../../../images/default_thumbnail.png";
import { __experimentalGetSettings, dateI18n } from "@wordpress/date";
import { cn } from "@blocks/utils";
import { upperFirst } from "lodash";
import { useSelect } from "@wordpress/data";

interface Props extends EditProps<any> {
	categories: Array<any>;
	posts: Array<any>;
	tags: Array<any>;
	numberOfPosts: number;
	page: number;
	author: Array<any>;
}

const Edit: React.ComponentType<Props> = (props) => {
	let isFirstPost = true;
	const {
		posts,
		className,
		attributes: {
			size,
			column,
			hideOnDesktop,
			presets,
			enableTitle,
			postTitleMarkup,
			enableDate,
			enableExcerpt,
			excerptLimit,
			enableReadMore,
			enableCategory,
			enableCategoryForHighlightPost,
			enableAuthor,
			readMoreText,
			separatorType,
			enableIcon,
			metaPosition,
			postCount,
		},
		setAttributes,
		categories,
		tags,
	} = props;

	const { clientId } = useClientId(props);
	const { deviceType } = useDeviceType();
	const { CopyPasterStyleBlockControl } = useCopyPasteStyles();
	const { Style } = useBlockStyle({
		blockName: "post-video",
		clientId,
		attributes: props.attributes,
		deviceType,
	});

	const postTypeOptions = useSelect((select) => {
		const { getPostTypes } = select("core") as any;
		const { getCurrentPost } = select("core/editor") as any;

		const allPostTypes = getPostTypes({ per_page: -1 }) || [];
		const post = getCurrentPost();
		const currentPostType = post?.type;
		const meta = post?.meta?._mzb_template;

		if (currentPostType === "mzb-builder-template") {
			return allPostTypes
				.filter((type: any) =>
					["post", "mzb-builder"].includes(type.slug)
				)
				.map((type: any) => {
					if (type.slug === "mzb-builder" && meta) {
						return {
							value: type.slug,
							label: `${upperFirst(meta)} Builder`,
						};
					}
					return {
						value: type.slug,
						label: type.name || type.slug,
					};
				});
		}

		return allPostTypes
			.filter((type: any) => type.slug !== "mzb-builder-template")
			.map((type: any) => ({
				value: type.slug,
				label: type.name || type.slug,
			}));
	}, []);

	const classNames = classnames(
		`mzb-post-video mzb-post-video-${clientId}`,
		size && `is-${size}`,
		className,
		hideOnDesktop && "magazine-blocks-hide-on-desktop",
		presets && `mzb-preset-${presets}`
	);

	const videoPosts = (posts || []).filter(
		(post: any) => post.format === "video"
	);

	const videoPostsCount = videoPosts.length;

	const TagName = postTitleMarkup || "h3";

	return (
		<Fragment>
			<InspectorControls
				attributes={props.attributes}
				setAttributes={setAttributes}
				categories={categories}
				tags={tags}
				postTypeOptions={postTypeOptions}
			/>
			<CopyPasterStyleBlockControl withBlockControls />
			<Style />
			<div className={classNames}>
				{
					<div
						className={`mzb-posts mzb-post-col--${column || "3"} ${
							presets === "style-3" && postCount === 1
								? "mzb-post-col--full"
								: ""
						}`}
					>
						<Fragment>
							{(posts || []).map((post, idx) => {
								const maxWords = excerptLimit;
								const excerpt = post?.excerpt?.rendered
									.split(" ")
									.slice(0, maxWords)
									.join(" ");

								const isThisFirstPost =
									idx ===
									videoPosts.findIndex(
										(p) => p.format === "video"
									);
								isFirstPost = false;

								if (post.format === "video") {
									return (
										<div
											className={`mzb-post ${
												isThisFirstPost
													? "mzb-first-video--highlight"
													: ""
											}`}
											key={idx}
										>
											<a
												className="mzb-video"
												href={post?.link}
											>
												{videoPostsCount > 0 &&
												post?.videoUrl ? (
													<video
														className="mzb-video-player"
														src={post?.videoUrl}
														controls
														poster={
															post
																?.magazine_blocks_featured_image_url
																?.full
																? post
																		?.magazine_blocks_featured_image_url
																		?.full[0]
																: defaultThumbnail
														}
													/>
												) : post
														?.magazine_blocks_featured_image_url
														?.full ? (
													<img
														className="mzb-featured-image"
														src={
															post
																?.magazine_blocks_featured_image_url
																?.full[0]
														}
													/>
												) : (
													<img
														className="mzb-featured-image"
														src={defaultThumbnail}
													/>
												)}
												<div className="mzb-image-overlay"></div>
												<div
													className="mzb-custom-embed-play"
													role="button"
												>
													<Icon
														type="frontendIcon"
														name="youtube-play"
														size={24}
													/>
												</div>
											</a>
											<div className="mzb-post-content">
												{enableCategoryForHighlightPost &&
													isThisFirstPost && (
														<div className="mzb-post-meta">
															<span
																className="mzb-post-categories"
																dangerouslySetInnerHTML={{
																	__html: post?.magazine_blocks_category,
																}}
															/>
														</div>
													)}
												{enableCategory &&
													!isThisFirstPost && (
														<div className="mzb-post-meta">
															<span
																className="mzb-post-categories"
																dangerouslySetInnerHTML={{
																	__html: post?.magazine_blocks_category,
																}}
															/>
														</div>
													)}
												{(presets !== "style-3" ||
													idx === 0) &&
													metaPosition === "top" && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableAuthor ===
																true && (
																<span className="mzb-post-author">
																	{enableIcon ===
																		true && (
																		<img
																			className="author-display-image"
																			src={
																				post?.magazine_blocks_author_image
																			}
																		/>
																	)}
																	<a
																		href={
																			post
																				?.magazine_blocks_author
																				?.author_link
																		}
																	>
																		{" "}
																		{
																			post
																				?.magazine_blocks_author
																				?.display_name
																		}{" "}
																	</a>
																</span>
															)}

															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}
												{enableTitle && (
													<TagName className="mzb-post-title">
														<a href={post?.link}>
															{
																post?.title
																	?.rendered
															}
														</a>
													</TagName>
												)}
												{(presets !== "style-3" ||
													idx === 0) &&
													metaPosition ===
														"bottom" && (
														<div
															className={cn(
																"mzb-post-entry-meta",
																separatorType &&
																	`mzb-meta-separator--${separatorType}`
															)}
														>
															{enableAuthor ===
																true && (
																<span className="mzb-post-author">
																	{enableIcon ===
																		true && (
																		<img
																			className="author-display-image"
																			src={
																				post?.magazine_blocks_author_image
																			}
																		/>
																	)}
																	<a
																		href={
																			post
																				?.magazine_blocks_author
																				?.author_link
																		}
																	>
																		{" "}
																		{
																			post
																				?.magazine_blocks_author
																				?.display_name
																		}{" "}
																	</a>
																</span>
															)}

															{enableDate ===
																true && (
																<span className="mzb-post-date">
																	{enableIcon ===
																		true && (
																		<Icon
																			type="blockIcon"
																			name="calendar"
																			size={
																				24
																			}
																		/>
																	)}
																	<a href="#">
																		{" "}
																		{dateI18n(
																			__experimentalGetSettings()
																				.formats
																				.date,
																			post?.date_gmt,
																			undefined
																		)}{" "}
																	</a>
																</span>
															)}
														</div>
													)}
												{(enableExcerpt ||
													enableReadMore) && (
													<div className="mzb-entry-content">
														{enableExcerpt && (
															<div
																className="mzb-entry-summary"
																dangerouslySetInnerHTML={{
																	__html: excerpt,
																}}
															/>
														)}
														{enableReadMore && (
															<div className="mzb-read-more">
																<a
																	href={
																		post?.link
																	}
																>
																	{
																		readMoreText
																	}
																</a>
															</div>
														)}
													</div>
												)}
											</div>
										</div>
									);
								}
							})}
						</Fragment>
					</div>
				}
			</div>
		</Fragment>
	);
};

// @ts-ignore
export default withSelect((select, props) => {
	const { getEntityRecords } = select("core");
	const { getCurrentPost } = select("core/editor");

	const {
		attributes: {
			category,
			tag,
			postCount,
			offset,
			order,
			orderBy,
			orderType,
			authorName,
			excludedCategory,
			postType,
		},
	} = props;

	const post = getCurrentPost();
	const isTemplate = post?.type === "mzb-builder-template";
	const isArchive = post?.type === "archive";
	const isSearch = post?.type === "search";

	const selectedPostType = isTemplate ? "post" : postType || "post";

	const shouldSkipFilters = isTemplate && (isArchive || isSearch);

	const query: any = {
		per_page: postCount,
		offset: offset || 0,
		orderby: orderBy,
		order: orderType,
		author: authorName,
	};

	if (!shouldSkipFilters) {
		if (category && category !== "all") {
			query.categories = parseInt(category);
		}
		if (tag && tag !== "all") {
			query.tags = tag;
		}
		if (excludedCategory && excludedCategory !== "") {
			query.categories_exclude = excludedCategory;
		}
	}

	return {
		posts: getEntityRecords("postType", selectedPostType, query),
		numberOfPosts:
			getEntityRecords("postType", selectedPostType, {
				per_page: -1,
				categories:
					"all" === category || selectedPostType !== "post"
						? undefined
						: parseInt(category),
			})?.length || 0,
		categories:
			getEntityRecords("taxonomy", "category", { per_page: -1 }) || [],
		tags: getEntityRecords("taxonomy", "post_tag", { per_page: -1 }) || [],
	};
})(Edit);
